(() => {
  const overlay = document.getElementById('HalloweenTransition');
  if (!overlay) return;
  const root = document.documentElement;
  const paint = overlay.querySelector('[data-ink-paint]');
  const fill = overlay.querySelector('[data-ink-fill]');
  const junction = overlay.querySelector('[data-ink-junction]');
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const key = 'lon:halloween-transition:v1';
  const lifecycle = new AbortController();
  const { signal } = lifecycle;
  const animations = new Set();
  let state = 'idle';
  let destination;
  let trigger;
  let navigateTimer;
  let deadlineTimer;
  let recoveryTimer;
  let arrivalTimer;
  let navigationFrame;

  const clearMarker = () => {
    try { sessionStorage.removeItem(key); } catch {}
  };

  const reset = ({ keepMarker = false } = {}) => {
    clearTimeout(navigateTimer);
    clearTimeout(deadlineTimer);
    clearTimeout(recoveryTimer);
    cancelAnimationFrame(navigationFrame);
    animations.forEach(animation => { try { animation.cancel(); } catch {} });
    animations.clear();
    overlay.hidden = true;
    overlay.classList.remove('is-covered');
    root.removeAttribute('data-halloween-outgoing');
    trigger?.removeAttribute('aria-busy');
    trigger = null;
    destination = null;
    state = 'idle';
    if (!keepMarker) clearMarker();
  };

  const releaseArrival = () => {
    clearTimeout(arrivalTimer);
    document.dispatchEvent(new Event('halloween:release'));
  };

  const openArrival = () => {
    if (!root.hasAttribute('data-halloween-incoming')) return;
    if (motion.matches || document.visibilityState === 'hidden') {
      releaseArrival();
      return;
    }
    root.setAttribute('data-halloween-incoming', 'opening');
    arrivalTimer = setTimeout(releaseArrival, 180);
  };

  const updateGeometry = () => {
    if (!paint || !fill || !junction) return false;
    const { width, height } = overlay.getBoundingClientRect();
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return false;
    const viewHeight = 1000 * height / width;
    paint.setAttribute('viewBox', `0 0 1000 ${viewHeight}`);
    fill.setAttribute('y', String(-viewHeight - 400));
    fill.setAttribute('height', String(viewHeight + 400));
    junction.setAttribute('height', String(viewHeight + 40));
    return true;
  };

  const navigate = () => {
    if (state !== 'covering') return;
    state = 'navigating';
    clearTimeout(navigateTimer);
    clearTimeout(deadlineTimer);
    overlay.classList.add('is-covered');
    const target = destination;
    try {
      const targetUrl = new URL(target);
      recoveryTimer = setTimeout(() => reset(), 4000);
      try { sessionStorage.setItem(key, JSON.stringify({ from: location.origin + location.pathname, url: targetUrl.origin + targetUrl.pathname + targetUrl.search, at: Date.now() })); }
      catch { reset(); }
      location.assign(target);
    } catch {
      reset();
    }
  };

  const cover = (link, url) => {
    root.setAttribute('data-halloween-outgoing', '');
    state = 'covering';
    destination = url.href;
    trigger = link;
    trigger.setAttribute('aria-busy', 'true');
    overlay.hidden = false;
    navigateTimer = setTimeout(() => {
      overlay.classList.add('is-covered');
      try { navigationFrame = requestAnimationFrame(() => { navigationFrame = requestAnimationFrame(navigate); }); }
      catch { navigate(); }
    }, 1100);
    deadlineTimer = setTimeout(navigate, 1300);
    const tracks = [
      [overlay.querySelector('[data-ink-body]'), 1100, 0, 'cubic-bezier(.45,0,.22,1)'],
      [overlay.querySelector('[data-ink-drip="left"]'), 900, 180, 'cubic-bezier(.64,0,.24,1)'],
      [overlay.querySelector('[data-ink-drip="center"]'), 1020, 0, 'cubic-bezier(.45,0,.22,1)'],
      [overlay.querySelector('[data-ink-drip="right"]'), 1040, 60, 'cubic-bezier(.48,0,.2,1)'],
    ];
    let started = 0;
    tracks.forEach(([element, duration, delay, easing]) => {
      try {
        const animation = element?.animate([
          { transform: 'translateY(-342px)' },
          { transform: 'translateY(103%)' },
        ], { duration, delay, easing, fill: 'forwards' });
        if (animation) {
          animations.add(animation);
          started++;
        }
      } catch {}
    });
    if (!started) {
      const target = destination;
      reset();
      try { location.assign(target); } catch {}
    }
  };

  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (state !== 'idle') {
      if (link === trigger && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) event.preventDefault();
      else if (link || event.target.closest?.('button,input,select,textarea,summary,[role="button"]')) reset();
      return;
    }
    if (!link?.hasAttribute('data-halloween-transition') || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || link.target && link.target !== '_self' || motion.matches || window.Shopify?.designMode || document.visibilityState === 'hidden') return;
    let url;
    try { url = new URL(link.href, location.href); } catch { return; }
    if (url.origin !== location.origin || url.protocol !== location.protocol || url.searchParams.get('view') !== 'halloween-2026') return;
    if (getComputedStyle(overlay).position !== 'fixed') return;
    if (!['[data-ink-body]', '[data-ink-drip="left"]', '[data-ink-drip="center"]', '[data-ink-drip="right"]'].every(selector => typeof overlay.querySelector(selector)?.animate === 'function')) return;
    try {
      const marker = JSON.stringify({ from: location.origin + location.pathname, url: url.origin + url.pathname + url.search, at: Date.now() });
      sessionStorage.setItem(key, marker);
      if (sessionStorage.getItem(key) !== marker) {
        clearMarker();
        return;
      }
    } catch {
      clearMarker();
      return;
    }
    overlay.hidden = false;
    if (!updateGeometry()) {
      overlay.hidden = true;
      clearMarker();
      return;
    }
    event.preventDefault();
    cover(link, url);
  }, { signal });

  document.addEventListener('submit', () => { if (state !== 'idle') reset(); }, { signal });
  window.addEventListener('resize', () => { if (state !== 'idle') updateGeometry(); }, { passive: true, signal });

  window.addEventListener('pagehide', () => {
    clearTimeout(arrivalTimer);
    reset({ keepMarker: state === 'navigating' });
  }, { signal });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    reset();
    releaseArrival();
  }, { signal });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') {
      if (state !== 'idle') reset();
      return;
    }
    if (state === 'covering') navigate();
    releaseArrival();
  }, { signal });
  motion.addEventListener('change', () => {
    if (!motion.matches) return;
    if (state === 'covering') navigate();
    reset({ keepMarker: state === 'navigating' });
    releaseArrival();
  }, { signal });

  if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', openArrival, { once: true, signal });
  else openArrival();
})();
